import { Link } from "react-router-dom";
import { FileText, CheckCircle, AlertCircle, Clock, Calendar, ChevronRight, Shield } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  'headline': 'Mobbing Protokoll: So dokumentieren Sie richtig',
  'description': 'Wie führe ich ein Mobbing-Protokoll richtig? Was muss rein, was zählt vor Gericht? Kostenlose Anleitung mit Vorlage für Arbeitnehmer und Schüler.',
  'author': { '@type': 'Organization', 'name': 'mobbing-melden.de' },
  'publisher': { '@type': 'Organization', 'name': 'mobbing-melden.de', 'url': 'https://www.mobbing-melden.de' },
  'datePublished': '2025-05-08',
  'inLanguage': 'de-DE',
  'mainEntityOfPage': { '@type': 'WebPage', '@id': 'https://www.mobbing-melden.de/blog/mobbing-protokoll' },
};

const WHY_ITEMS = [
  { emoji: "⚖️", text: "Arbeitsgerichte und Betriebsräte verlangen konkrete Fakten — ohne Protokoll ist Mobbing kaum beweisbar." },
  { emoji: "🔍", text: "Ein Protokoll zeigt Muster: Was wie eine Kleinigkeit wirkt, wird im Gesamtbild zur klaren Systematik." },
  { emoji: "🧠", text: "Schreiben entlastet. Viele Betroffene berichten, dass das strukturierte Festhalten das Gefühl der Ohnmacht reduziert." },
  { emoji: "📋", text: "Es dient als Grundlage für Gespräche mit Anwalt, Betriebsrat, Arzt oder Polizei." },
];

const MUST_HAVES = [
  { field: "Datum & Uhrzeit", detail: "So genau wie möglich — 15.04.2025, 09:30 Uhr, nicht \"Anfang April\"" },
  { field: "Ort", detail: "Büro, Kantine, Gruppen-Chat, Telefon, E-Mail, Schulflur …" },
  { field: "Beteiligte", detail: "Vollständiger Name + Funktion (Kollege, Teamleiter, Mitschüler)" },
  { field: "Was wurde getan/gesagt?", detail: "Möglichst wörtlich — in Anführungszeichen. Keine Interpretation, nur Tatsachen." },
  { field: "Zeugen", detail: "Wer war anwesend? Hat jemand etwas gesagt oder getan?" },
  { field: "Reaktion", detail: "Was haben Sie gesagt oder getan? Haben Sie widersprochen, sind Sie gegangen?" },
  { field: "Auswirkungen", detail: "Schlafprobleme, Magenschmerzen, Weinen, Angst vor dem nächsten Tag …" },
  { field: "Anhänge", detail: "Referenz auf Screenshots, E-Mails oder Fotos, die Sie separat gespeichert haben" },
];

const MISTAKES = [
  {
    mistake: "Zu lange warten",
    fix: "Schreiben Sie den Eintrag noch am selben Tag. Eine direkt notierte Aussage ist glaubwürdiger als eine, die drei Wochen später rekonstruiert wird.",
  },
  {
    mistake: "Wertungen statt Fakten",
    fix: "Schreiben Sie nicht \"Er hat mich wieder gemobbt\", sondern: \"Er sagte wörtlich: '...'\" — sachlich, präzise, ohne Interpretation.",
  },
  {
    mistake: "Nur auf dem Arbeits-PC speichern",
    fix: "Der Arbeitgeber hat Zugriff auf Firmen-Geräte. Nutzen Sie ausschließlich Ihr privates Gerät oder einen passwortgeschützten Cloud-Speicher.",
  },
  {
    mistake: "Kleine Vorfälle weglassen",
    fix: "Auch der augenscheinlich harmlose Kommentar gehört rein. Mobbing lebt von der Häufung — erst die Summe macht das Muster sichtbar.",
  },
  {
    mistake: "Protokoll dem Täter zeigen",
    fix: "Das Protokoll bleibt vertraulich bis zu dem Zeitpunkt, an dem Sie es bewusst einsetzen wollen. Niemals vorab zeigen.",
  },
];

const FORMATS = [
  {
    title: "Digitales Textdokument",
    pros: ["Einfach zu durchsuchen", "Backup in der Cloud", "Schnell ergänzbar"],
    cons: ["Braucht Disziplin — keine Erinnerung"],
    tip: "Google Docs oder Word auf privatem Gerät. Teilen Sie es mit niemandem außer Ihrem Anwalt.",
  },
  {
    title: "Notizbuch (handschriftlich)",
    pros: ["Glaubwürdig als Originaldokument", "Kein Hacking möglich", "Immer dabei"],
    cons: ["Nicht durchsuchbar", "Kann verloren gehen"],
    tip: "Nummerieren Sie die Seiten und schreiben Sie fortlaufend — keine Lücken.",
  },
  {
    title: "Online-Formular (mobbing-melden.de)",
    pros: ["Strukturiert geführt", "Automatisches PDF", "Anonym & sicher"],
    cons: ["Kein Offline-Zugriff"],
    tip: "Am besten für die offizielle Meldung — ergänzt Ihr persönliches Protokoll.",
  },
];

export default function BlogArtikelProtokoll() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing Protokoll: So dokumentieren Sie richtig (2025)"
        description="Wie führe ich ein Mobbing-Protokoll richtig? Was muss rein, was zählt vor Gericht? Kostenlose Anleitung mit Muster-Vorlage für Arbeitnehmer und Schüler in Deutschland."
        path="/blog/mobbing-protokoll"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#A0AEC0] mb-8">
            <Link to="/" className="hover:text-[#2D3748]">Start</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#2D3748]">Ratgeber</Link>
            <span>/</span>
            <span className="text-[#2D3748]">Mobbing Protokoll</span>
          </nav>

          {/* Article header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="bg-[#BDE0FE]/40 text-[#1A365D] text-xs font-bold px-3 py-1 rounded-full">Dokumentation</span>
              <span className="flex items-center gap-1 text-[#A0AEC0] text-xs"><Clock className="w-3 h-3" /> 6 Min Lesezeit</span>
              <span className="flex items-center gap-1 text-[#A0AEC0] text-xs"><Calendar className="w-3 h-3" /> Mai 2025</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4 leading-tight" style={{ fontFamily: 'Nunito' }}>
              Mobbing Protokoll: So dokumentieren Sie richtig
            </h1>
            <p className="text-lg text-[#718096] leading-relaxed">
              Ein Mobbing-Protokoll ist das wichtigste Werkzeug für Betroffene. Es entscheidet, ob Ihr Fall vor Gericht, beim Betriebsrat oder bei der Polizei erfolgreich ist — oder ob er im Sand verläuft. Hier erfahren Sie genau, was rein muss und wie Sie es führen.
            </p>
          </header>

          {/* CTA */}
          <div className="bg-[#BDE0FE]/20 border border-[#BDE0FE] rounded-2xl p-5 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#2D3748] font-medium">Kein Word-Dokument nötig: Protokoll direkt online erstellen — kostenlos & anonym.</p>
            <Link to="/melden" className="inline-flex items-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-5 py-2.5 rounded-full hover:bg-[#86D696] transition-all text-sm whitespace-nowrap">
              Jetzt erstellen <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Why */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Warum ein Protokoll unverzichtbar ist</h2>
            <p className="text-[#718096] mb-5 leading-relaxed">
              Das häufigste Problem in Mobbing-Fällen: Betroffene warten zu lange. Sie hoffen, es hört von selbst auf. Oder sie haben Angst, es zu eskalieren. Jede Woche ohne Protokoll ist eine Woche wertvoller Beweise, die unwiederbringlich verloren ist.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {WHY_ITEMS.map((item, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex gap-3 text-sm text-[#718096]">
                  <span className="text-xl flex-shrink-0">{item.emoji}</span>
                  {item.text}
                </div>
              ))}
            </div>
          </section>

          {/* What goes in */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Was ins Protokoll gehört — Pflichtfelder</h2>
            <p className="text-[#718096] mb-5 leading-relaxed">
              Für jeden Vorfall einen eigenen, datierten Eintrag. Diese Felder sind Pflicht:
            </p>
            <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 overflow-hidden">
              <div className="bg-[#2D3748] text-white px-5 py-2.5 text-xs font-bold tracking-wide">
                EINTRAG — [Datum] — [Laufende Nummer]
              </div>
              {MUST_HAVES.map((item, i) => (
                <div key={i} className={`flex gap-4 px-5 py-3 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <span className="font-bold text-[#2D3748] w-44 flex-shrink-0">{item.field}</span>
                  <span className="text-[#718096] italic">{item.detail}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Example entry */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Beispiel-Eintrag</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 font-mono text-sm text-[#4A5568] leading-relaxed">
              <p className="font-bold text-[#2D3748] mb-3">Eintrag #7 — 15.04.2025, 09:30 Uhr</p>
              <p><strong>Ort:</strong> Großraumbüro, Abteilung Marketing, Tisch von K. Meyer</p>
              <p className="mt-2"><strong>Beteiligte:</strong> K. Meyer (Teamleiter), J. Bauer (Kollegin, anwesend)</p>
              <p className="mt-2"><strong>Vorfall:</strong> K. Meyer kam zu meinem Tisch und sagte laut, sodass J. Bauer es hören konnte: "Ihr Bericht ist wie immer unbrauchbar. Ich weiß nicht, warum wir Sie überhaupt noch beschäftigen."</p>
              <p className="mt-2"><strong>Reaktion:</strong> Ich habe nicht geantwortet und bin nach 10 Minuten auf die Toilette gegangen.</p>
              <p className="mt-2"><strong>Zeugen:</strong> J. Bauer war anwesend, hat nichts gesagt.</p>
              <p className="mt-2"><strong>Auswirkungen:</strong> Konnte den Rest des Tages nicht konzentriert arbeiten. Abends Kopfschmerzen, Schlafprobleme.</p>
              <p className="mt-2"><strong>Anhänge:</strong> Keiner für diesen Vorfall.</p>
            </div>
            <p className="text-[#A0AEC0] text-xs mt-2 text-center">Muster-Eintrag — Namen sind frei erfunden</p>
          </section>

          {/* Mistakes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>5 häufige Fehler beim Protokollführen</h2>
            <div className="space-y-3">
              {MISTAKES.map((item, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-[#C53030] font-bold text-sm flex-shrink-0">✗</span>
                    <span className="font-bold text-[#2D3748] text-sm">{item.mistake}</span>
                  </div>
                  <div className="flex items-start gap-2 ml-4">
                    <span className="text-[#1A4731] font-bold text-sm flex-shrink-0">→</span>
                    <span className="text-[#718096] text-sm">{item.fix}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Formats */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Welches Format ist das beste?</h2>
            <div className="space-y-4">
              {FORMATS.map((fmt, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="font-bold text-[#2D3748] mb-3">{fmt.title}</h3>
                  <div className="grid md:grid-cols-2 gap-3 mb-3 text-sm">
                    <div>
                      <p className="text-[#A0AEC0] text-xs font-bold mb-1">VORTEILE</p>
                      {fmt.pros.map((p, j) => <p key={j} className="text-[#4A5568] flex items-center gap-1"><CheckCircle className="w-3 h-3 text-[#1A4731]" /> {p}</p>)}
                    </div>
                    <div>
                      <p className="text-[#A0AEC0] text-xs font-bold mb-1">NACHTEILE</p>
                      {fmt.cons.map((c, j) => <p key={j} className="text-[#4A5568] flex items-center gap-1"><AlertCircle className="w-3 h-3 text-[#C53030]" /> {c}</p>)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-2 text-xs text-[#718096]">
                    💡 <strong>Tipp:</strong> {fmt.tip}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* When to submit */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Wann reicht man das Protokoll ein?</h2>
            <p className="text-[#718096] mb-4 leading-relaxed">
              Das Protokoll ist kein Selbstzweck — es ist die Vorbereitung für den nächsten Schritt. Typische Situationen:
            </p>
            <ul className="space-y-2 text-sm text-[#4A5568]">
              {[
                "Nach 2–4 Wochen dokumentierter Vorfälle: Beschwerde beim Betriebsrat einreichen",
                "Bei akuter Eskalation: Sofortige Meldung, auch ohne lange Vorgeschichte",
                "Vor dem Anwaltsgespräch: Protokoll mitbringen — das spart Beratungszeit und Geld",
                "Als Beweismittel im Arbeitsgerichtsprozess: Protokoll + Zeugenaussagen",
                "Bei der Polizei: Bei Bedrohungen oder Beleidigungen, die strafrechtlich relevant sind",
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#A0AEC0] flex-shrink-0 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </section>

          {/* Fazit */}
          <section className="mb-10 bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>Fazit</h2>
            <p className="text-[#718096] leading-relaxed">
              Ein gutes Mobbing-Protokoll ist sachlich, vollständig und konsequent. Es zeigt nicht nur einzelne Vorfälle — es zeigt das System dahinter. Beginnen Sie heute: Legen Sie ein Dokument an, tragen Sie den letzten Vorfall ein und führen Sie es von nun an konsequent weiter. Das ist der effektivste erste Schritt, den Sie tun können.
            </p>
          </section>

          {/* Related */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Weiterführende Ratgeber</h2>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { emoji: "🔍", label: "Beweise sichern", path: "/mobbing-beweise" },
                { emoji: "👥", label: "Betriebsrat einschalten", path: "/betriebsrat" },
                { emoji: "👔", label: "Mobbing am Arbeitsplatz", path: "/blog/mobbing-am-arbeitsplatz" },
              ].map((item, i) => (
                <Link key={i} to={item.path} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 hover:border-gray-300 transition-colors text-sm font-medium text-[#2D3748]">
                  <span className="text-xl">{item.emoji}</span>
                  {item.label}
                  <ChevronRight className="w-4 h-4 text-[#A0AEC0] ml-auto" />
                </Link>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#EBF8FF] to-[#E6FFFA] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Protokoll jetzt online erstellen
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Kein Download, kein Word, keine Registrierung. Formular ausfüllen — strukturiertes PDF-Protokoll erhalten. Anonym und kostenlos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all">
                <FileText className="w-5 h-5" />
                Protokoll erstellen
              </Link>
              <Link to="/mobbing-protokoll" className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-8 py-4 rounded-full hover:bg-[#BDE0FE]/20 transition-all">
                Vorlage ansehen →
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
